/**
 * SportsClubNew — /sports/clubs/new
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users, Save, Image as ImageIcon } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SportsCategoryNav from "@/components/SportsCategoryNav";
import VideoLinkManager from "@/components/VideoLinkManager";

const empty = {
  name: "", sport: "", description: "",
  city: "", state: "", country: "",
  founded_year: "", logo_url: "", hero_image_url: "",
  contact_email: "", contact_phone: "", website: "",
  photo_urls: "",
};

export default function SportsClubNew() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [videoUrls, setVideoUrls] = useState([]);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/sports/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Sign in to register a club.");
      nav(`/login?redirect=${encodeURIComponent("/sports/clubs/new")}`, { replace: true });
    }
  }, [authLoading, user, nav]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sport) { toast.error("Name and sport are required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), sport: form.sport,
        description: form.description || null,
        city: form.city || null, state: form.state || null, country: form.country || null,
        founded_year: form.founded_year ? Number(form.founded_year) : null,
        logo_url: form.logo_url || null,
        hero_image_url: form.hero_image_url || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        website: form.website || null,
        photo_urls: form.photo_urls ? form.photo_urls.split(/[\n,]/).map(s => s.trim()).filter(Boolean) : [],
        video_urls: videoUrls,
      };
      const r = await http.post("/sports/clubs", payload);
      toast.success("Club registered!");
      nav(`/sports/clubs/${r.data.club.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create club.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <SportsCategoryNav />
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="club-new-page">
        <div className="overline mb-1">NEW CLUB</div>
        <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2">
          <Users className="w-8 h-8" />Register your club
        </h1>
        <p className="opacity-70 mb-6">A public page for your team, ready in under a minute.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Club name *">
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="wofy-input" data-testid="input-name" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Primary sport *">
              <select required value={form.sport} onChange={e => set("sport", e.target.value)} className="wofy-input" data-testid="input-sport">
                <option value="">Select a sport…</option>
                {meta?.categories?.map(cat => (
                  <optgroup key={cat.key} label={cat.label}>
                    {cat.sports.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>
            <Field label="Founded year">
              <input type="number" min="1800" max="2099" value={form.founded_year} onChange={e => set("founded_year", e.target.value)} className="wofy-input" data-testid="input-founded" />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={4} value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="History, achievements, what you're about…"
              className="wofy-input" data-testid="input-description" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="City"><input value={form.city} onChange={e => set("city", e.target.value)} className="wofy-input" data-testid="input-city" /></Field>
            <Field label="State"><input value={form.state} onChange={e => set("state", e.target.value)} className="wofy-input" data-testid="input-state" /></Field>
            <Field label="Country"><input value={form.country} onChange={e => set("country", e.target.value)} className="wofy-input" data-testid="input-country" /></Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Logo URL"><input value={form.logo_url} onChange={e => set("logo_url", e.target.value)} className="wofy-input" data-testid="input-logo" /></Field>
            <Field label="Hero image URL"><input value={form.hero_image_url} onChange={e => set("hero_image_url", e.target.value)} className="wofy-input" data-testid="input-hero" /></Field>
          </div>

          {/* Photos + Videos, side-by-side */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2 inline-flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" /> Upload photos (URLs, one per line)
              </div>
              <textarea rows={4} value={form.photo_urls} onChange={e => set("photo_urls", e.target.value)}
                placeholder="https://…jpg" className="wofy-input font-mono text-xs" data-testid="input-photos" />
              <p className="text-xs opacity-60 mt-1">Up to 12 photos.</p>
            </div>
            <div>
              <VideoLinkManager value={videoUrls} onChange={setVideoUrls} max={8}
                helper="Paste YouTube or Vimeo links (match highlights, training, promo)." />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Contact email"><input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className="wofy-input" data-testid="input-email" /></Field>
            <Field label="Contact phone"><input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} className="wofy-input" data-testid="input-phone" /></Field>
            <Field label="Website"><input value={form.website} onChange={e => set("website", e.target.value)} className="wofy-input" data-testid="input-website" /></Field>
          </div>

          <div className="pt-2">
            <button disabled={saving} className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Register club
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
